function toStr(v) { return (v?._id || v || '').toString(); }

/** Liga-Projekt — playertools/Kader (auch bei Cup-Partien). */
export const ROSTER_PLAYertools_PROJECT_ID = '6a2f9d9919ceff70b90723d7';
/** FC Wacker Innsbruck — immer playertools, nie match_players. */
export const FCW_TEAM_ID = '6856423e9c6db6997ce29ce5';

export function getPlayertoolsProjectId(_gameProjectId) {
    return ROSTER_PLAYertools_PROJECT_ID;
}

export function isOwnTeamRoster(teamId, trackedTeamId) {
    const tid = toStr(teamId);
    if (tid === FCW_TEAM_ID) return true;
    if (trackedTeamId && tid === toStr(trackedTeamId)) return true;
    return false;
}

/** Position type 2 = Betreuer; also exclude staff roles mis-tagged as type 1. */
export function isBetreuerEntry(entry) {
    const pos = entry?.plid?.posid;
    if (!pos || typeof pos !== 'object') return false;
    if (Number(pos.type) === 2) return true;
    const name = (pos.name || '').toLowerCase();
    return /trainer|betreuer|physio|zeugwart|manager|arzt|therapeut/.test(name);
}

export function isMatchPlayerEntry(entry) {
    return !!(entry?.matchPlayerId || entry?.matchPlayer?._id);
}

export function lineupPlayerKey(entry) {
    if (!entry) return '';
    const mpId = toStr(entry.matchPlayerId?._id || entry.matchPlayerId || entry.matchPlayer?._id);
    if (mpId) return `mp:${mpId}`;
    return `pl:${toStr(entry.plid?._id || entry.plid)}`;
}

export function rosterEntryKey(entry) {
    if (!entry) return '';
    if (entry.source === 'matchplayer' || entry.matchPlayerId) {
        const mp = toStr(entry.matchPlayerId?._id || entry.matchPlayerId);
        if (mp) return `mp:${mp}`;
    }
    const pl = toStr(entry.plid?._id || entry.plid);
    if (pl) return `pl:${pl}`;
    if (entry._id) return `pt:${toStr(entry._id)}`;
    return '';
}

export function lineupToRosterKey(entry) {
    if (!entry) return '';
    if (entry.matchPlayerId) {
        return rosterEntryKey({ source: 'matchplayer', matchPlayerId: entry.matchPlayerId });
    }
    if (entry.plid) {
        return rosterEntryKey({ source: 'playertool', plid: entry.plid });
    }
    return entry._id ? `gp:${toStr(entry._id)}` : '';
}

export function mergeRosterEntries(...lists) {
    const byKey = new Map();
    lists.flat().forEach((entry) => {
        if (!entry) return;
        const key = rosterEntryKey(entry);
        if (!key) return;
        byKey.set(key, entry);
    });
    return [...byKey.values()];
}

export function getLineupPlayerProfile(entry) {
    if (!entry) return { prename: '', surname: '', num: null };
    if (entry.matchPlayerId && typeof entry.matchPlayerId === 'object') {
        return {
            prename: entry.matchPlayerId.prename || '',
            surname: entry.matchPlayerId.surname || '',
            num: entry.num ?? entry.matchPlayerId.num ?? null,
        };
    }
    if (entry.matchPlayer) {
        return {
            prename: entry.matchPlayer.prename || '',
            surname: entry.matchPlayer.surname || '',
            num: entry.num ?? entry.matchPlayer.num ?? null,
        };
    }
    return {
        prename: entry.plid?.prename || '',
        surname: entry.plid?.surname || '',
        num: entry.num ?? null,
    };
}

export function playerDisplayNameFromEntry(entry) {
    const profile = getLineupPlayerProfile(entry);
    const pre = profile.prename ? `${profile.prename[0]}. ` : '';
    return `${pre}${profile.surname}`.trim() || '?';
}

export function matchPlayerToRosterEntry(mp, tid) {
    const teamId = toStr(tid || mp.tid?._id || mp.tid);
    const id = toStr(mp._id || mp.matchPlayerId);
    return {
        source: 'matchplayer',
        tid: teamId,
        num: mp.num ?? null,
        matchPlayerId: id,
        plid: {
            _id: id,
            prename: mp.prename || '',
            surname: mp.surname || '',
        },
    };
}

export function normalizePlayertoolEntry(pt, fallbackTid) {
    return {
        ...pt,
        source: 'playertool',
        tid: toStr(pt.tid?._id || pt.tid || fallbackTid),
    };
}

export function rosterSearchText(entry) {
    const profile = getLineupPlayerProfile(entry);
    return `${entry.num ?? profile.num ?? ''} ${profile.prename || ''} ${profile.surname || ''}`.toLowerCase();
}

export function rosterDisplayLabel(entry) {
    const profile = getLineupPlayerProfile(entry);
    const num = entry.num ?? profile.num;
    const numPrefix = num ? `#${num} ` : '';
    const name = profile.surname
        ? `${profile.surname}${profile.prename ? `, ${profile.prename}` : ''}`
        : '?';
    return `${numPrefix}${name}`.trim();
}

export function parsePlayerKey(key) {
    const value = String(key || '');
    if (value.startsWith('mp:')) return { type: 'matchplayer', id: value.slice(3) };
    if (value.startsWith('pl:')) return { type: 'player', id: value.slice(3) };
    return { type: 'player', id: value };
}

export function isStarterEntry(entry) {
    if (entry.isStarter === true) return true;
    if (entry.isStarter === false) return false;
    return entry.type !== 1;
}

export function getTeamLineup(lineup, teamId) {
    return (lineup || []).filter((entry) => toStr(entry.tid) === toStr(teamId));
}

export function getOnFieldPlayers(lineup, teamId, subbedOutIds = []) {
    const outSet = new Set(subbedOutIds.map(toStr));
    return getTeamLineup(lineup, teamId).filter(
        (entry) => isStarterEntry(entry) && !outSet.has(lineupPlayerKey(entry))
    );
}

export function getBenchPlayers(lineup, teamId, subbedInIds = []) {
    const inSet = new Set(subbedInIds.map(toStr));
    return getTeamLineup(lineup, teamId).filter(
        (entry) => !isStarterEntry(entry) && !inSet.has(lineupPlayerKey(entry))
    );
}

export function playerLabel(entry, num = '') {
    const profile = getLineupPlayerProfile(entry);
    const pre = profile.prename ? `${profile.prename[0]}. ` : '';
    const jersey = num || profile.num || entry.num || '';
    return `${jersey ? `#${jersey} ` : ''}${pre}${profile.surname}`.trim();
}

export function playerDisplayName(entry) {
    return playerDisplayNameFromEntry(entry);
}

export function applySubstitutionToLineup(lineup, playerOutKey, playerInKey) {
    const outKey = toStr(playerOutKey);
    const inKey = toStr(playerInKey);
    return lineup.map((entry) => {
        const key = lineupPlayerKey(entry);
        if (key === outKey || key === `pl:${outKey}` || key === `mp:${outKey}`) {
            return { ...entry, type: 1, isStarter: false };
        }
        if (key === inKey || key === `pl:${inKey}` || key === `mp:${inKey}`) {
            return { ...entry, type: 0, isStarter: true };
        }
        return entry;
    });
}

export function revertSubstitutionFromLineup(lineup, playerOutKey, playerInKey) {
    const outKey = toStr(playerOutKey);
    const inKey = toStr(playerInKey);
    return lineup.map((entry) => {
        const key = lineupPlayerKey(entry);
        if (key === outKey || key === `pl:${outKey}` || key === `mp:${outKey}`) {
            return { ...entry, type: 0, isStarter: true };
        }
        if (key === inKey || key === `pl:${inKey}` || key === `mp:${inKey}`) {
            return { ...entry, type: 1, isStarter: false };
        }
        return entry;
    });
}

export function getTickerEventPlayerKey(event) {
    const mp = toStr(event?.matchPlayerId?._id || event?.matchPlayerId);
    if (mp) return `mp:${mp}`;
    const pl = toStr(event?.plid?._id || event?.plid);
    return pl ? `pl:${pl}` : '';
}

export function getTickerEventPlayerOutKey(event) {
    const mp = toStr(event?.matchPlayerOut?._id || event?.matchPlayerOut);
    if (mp) return `mp:${mp}`;
    const pl = toStr(event?.playerOut?._id || event?.playerOut);
    if (pl) return `pl:${pl}`;
    return getTickerEventPlayerKey(event);
}

export function getTickerEventPlayerInKey(event) {
    const mp = toStr(event?.matchPlayerIn?._id || event?.matchPlayerIn);
    if (mp) return `mp:${mp}`;
    const pl = toStr(event?.playerIn?._id || event?.playerIn);
    return pl ? `pl:${pl}` : '';
}

function eventPlayerOutKey(event) {
    const mp = toStr(event.matchPlayerOut?._id || event.matchPlayerOut);
    if (mp) return `mp:${mp}`;
    const pl = toStr(event.playerOut?._id || event.playerOut || event.plid?._id || event.plid);
    return pl ? `pl:${pl}` : '';
}

function eventPlayerInKey(event) {
    const mp = toStr(event.matchPlayerIn?._id || event.matchPlayerIn);
    if (mp) return `mp:${mp}`;
    const pl = toStr(event.playerIn?._id || event.playerIn);
    return pl ? `pl:${pl}` : '';
}

export function getSubbedOutIds(events, teamId) {
    return (events || [])
        .filter((event) => event.type === 5 && toStr(event.tid) === toStr(teamId))
        .map(eventPlayerOutKey)
        .filter(Boolean);
}

export function getSubbedInIds(events, teamId) {
    return (events || [])
        .filter((event) => event.type === 5 && toStr(event.tid) === toStr(teamId))
        .map(eventPlayerInKey)
        .filter(Boolean);
}

export function buildTickerPlayerPayload(key) {
    const parsed = parsePlayerKey(key);
    if (parsed.type === 'matchplayer') {
        return { matchPlayerId: parsed.id, plid: null };
    }
    return { plid: parsed.id, matchPlayerId: null };
}

export function buildTickerSubPayload(outKey, inKey) {
    const out = parsePlayerKey(outKey);
    const inn = parsePlayerKey(inKey);
    const payload = {};
    if (out.type === 'matchplayer') {
        payload.matchPlayerOut = out.id;
        payload.matchPlayerId = out.id;
        payload.plid = null;
        payload.playerOut = null;
    } else {
        payload.playerOut = out.id;
        payload.plid = out.id;
        payload.matchPlayerOut = null;
        payload.matchPlayerId = null;
    }
    if (inn.type === 'matchplayer') {
        payload.matchPlayerIn = inn.id;
        payload.playerIn = null;
    } else {
        payload.playerIn = inn.id;
        payload.matchPlayerIn = null;
    }
    return payload;
}

export { toStr };
